/**
 * Blog posts — content lives here so writing / editing stays in one file.
 *
 * Each post = { slug, title, description, date, author, readMins, cover, coverAlt,
 *               category, keywords, sections: [ { type, ... } ] }
 *
 * Section types the renderer understands:
 *   h2:      { type:'h2', text }
 *   h3:      { type:'h3', text }
 *   p:       { type:'p', text }        // text can include **bold** and [label](url)
 *   ul / ol: { type:'ul'|'ol', items:[string] }
 *   quote:   { type:'quote', text, cite? }
 *   callout: { type:'callout', title?, text }
 *   table:   { type:'table', headers:[string], rows:[[string]] }
 *   cta:     { type:'cta' }            // renders a "Start trial" button
 */

export const POSTS = [
  {
    slug: "why-indian-dental-clinics-choose-viyora-2026",
    title: "Why Indian dental clinics are moving to Viyora in 2026",
    description:
      "GST-compliant invoicing, WhatsApp reminders, Viyora Copilot and DPDP-ready storage — the practical reasons Indian dentists are switching to Viyora practice management software.",
    date: "2026-02-04",
    author: "Team Viyora",
    readMins: 6,
    category: "Product",
    cover:
      "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODM4MTAxMDV8MA&ixlib=rb-4.1.0&q=85&w=1600",
    coverAlt: "Modern Indian dental clinic interior using Viyora software",
    keywords:
      "dental clinic management software India, dental practice management software, GST dental invoice, WhatsApp appointment reminders dentists, AI dental software, Viyora review",
    sections: [
      {
        type: "p",
        text:
          "Running a modern dental practice in India in 2026 looks nothing like it did five years ago. Patients book on WhatsApp, expect a UPI link in the chat, and quietly compare your clinic against three others on Google before walking in. Your software should keep up — most legacy tools do not. Viyora was built specifically for how Indian clinics actually work.",
      },
      { type: "h2", text: "1. GST invoicing that a CA will actually accept" },
      {
        type: "p",
        text:
          "Every Viyora invoice is a proper Tax Invoice: sequential invoice number, HSN codes, CGST/SGST/IGST split by the patient's state of supply, and rendered as a print-ready PDF via WeasyPrint. No plugins, no manual formatting — hand the PDF to your accountant and it just works.",
      },
      { type: "h2", text: "2. WhatsApp reminders that patients actually read" },
      {
        type: "p",
        text:
          "Viyora sends reminders on the official WhatsApp Business API — 24 hours and 2 hours before each appointment. WhatsApp messages have a 98% open rate versus 20% for SMS (Meta, 2024), which is why almost every clinic on Viyora sees a sharp drop in no-shows within the first quarter.",
      },
      { type: "h2", text: "3. An Viyora Copilot built for chairside, not a chatbot" },
      {
        type: "p",
        text:
          "Viyora Copilot is a purpose-built AI assistant for dental workflows. It drafts treatment plans from your notes, summarises a patient's full history in one paragraph before you walk in, and answers WhatsApp queries when your receptionist is on leave. No copy-paste to ChatGPT, no PHI leaving your clinic.",
      },
      { type: "h2", text: "4. DPDP Act 2023 compliance out of the box" },
      {
        type: "p",
        text:
          "The Digital Personal Data Protection Act came into force in phases through 2024–25 and every dental clinic is a Data Fiduciary under it. Viyora ships one-click data export (portability), record-level erasure, encrypted-at-rest storage on Indian servers, and a signed Data Fiduciary agreement on onboarding.",
      },
      { type: "h2", text: "5. Everything for one price, in INR" },
      {
        type: "p",
        text:
          "Viyora's Solo plan is ₹799/month for a single-dentist practice. The Clinic plan is ₹1,499/month for multi-doctor setups — including unlimited team members, WhatsApp reminders, X-ray storage, and unlimited Viyora Copilot. No per-user pricing, no add-on modules, no annual contract.",
      },
      {
        type: "callout",
        title: "The switch is boring, in a good way",
        text:
          "Our success team migrates your existing patient database from Excel, CSV or your current software during the free trial. Most clinics are fully live in under three working days.",
      },
      { type: "h2", text: "What clinics tell us after 90 days" },
      {
        type: "ul",
        items: [
          "Front-desk saves ~5 hours a week on manual reminder calls.",
          "GST filings that used to take a Saturday now take an hour.",
          "New-patient conversion improves once online booking is live on Google.",
          "Doctors stop dictating notes at night — Copilot does the first draft.",
        ],
      },
      { type: "cta" },
    ],
  },

  {
    slug: "switching-to-viyora-from-legacy-dental-software",
    title: "Switching to Viyora from legacy dental practice-management software",
    description:
      "What changes when you move your clinic to Viyora — pricing model, WhatsApp automation, GST invoicing, Viyora Copilot and DPDP readiness — and how migration works during the free trial.",
    date: "2026-01-28",
    author: "Team Viyora",
    readMins: 8,
    category: "Guide",
    cover:
      "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
    coverAlt: "Two dentists comparing patient records on a tablet",
    keywords:
      "switch dental software India, migrate dental clinic software, best dental practice management India, modern dental EMR, dental software migration",
    sections: [
      {
        type: "p",
        text:
          "Most Indian dental clinics still run on general-purpose clinic software or Excel — tools that were never designed for how dentistry actually works. Viyora is different: dental-first, India-first, and shipped with an Viyora Copilot in the box. This is a straightforward look at what changes when you move.",
      },
      {
        type: "callout",
        title: "How this guide is written",
        text:
          "We describe Viyora against the general shape of legacy dental practice-management tools available in India today — the kind of software most clinics inherit or buy without a proper trial. If your current tool does something better than described here, write to us at admin@viyora.co.in and we'll update the post.",
      },
      { type: "h2", text: "The short version" },
      {
        type: "ul",
        items: [
          "Viyora is dental-first. Most legacy tools are general clinic software that also serve dentists.",
          "Viyora publishes flat monthly pricing in INR. Legacy tools usually do not publish pricing — you have to sit through a sales call.",
          "Viyora includes a built-in Viyora Copilot at every plan. Legacy tools do not currently ship a chairside AI feature.",
          "Both offer appointment reminders. Viyora uses the official WhatsApp Business API by default; legacy tools are usually SMS-first.",
          "Both can produce GST invoices. Viyora renders CA-ready PDFs out of the box.",
        ],
      },
      { type: "h2", text: "Feature-by-feature" },
      {
        type: "table",
        headers: ["Feature", "Viyora", "Typical legacy tool"],
        rows: [
          ["Built specifically for dental", "Yes", "General clinic tool"],
          ["Flat monthly pricing in INR", "₹799 / ₹1,499 per month, published", "Not published — contact sales"],
          ["WhatsApp reminders (official Business API)", "Included on all plans", "Usually SMS-first, WhatsApp optional"],
          ["GST-compliant PDF invoicing", "Yes (WeasyPrint, HSN codes)", "Available"],
          ["Viyora Copilot (chairside)", "Built in on every plan", "Not currently offered"],
          ["Digital consent forms", "Yes, tokenised patient links", "Available"],
          ["X-ray & document storage", "Included, encrypted at rest", "Available"],
          ["Online booking widget", "Public booking page per clinic", "Marketplace-dependent"],
          ["Prescription pad (Rx PDF)", "Yes, with drug database", "Yes"],
          ["Multi-branch / multi-doctor", "Included in Clinic plan", "Available"],
          ["DPDP Act 2023 workflow", "Data-Fiduciary agreement + one-click export", "Standard privacy policy"],
          ["Free trial", "30 days, no credit card", "Demo on request"],
        ],
      },
      { type: "h2", text: "Where legacy tools are genuinely strong" },
      {
        type: "p",
        text:
          "Legacy dental software has been around for a decade or more, and that maturity shows in polished edge-case workflows — things like insurance-claim quirks, unusual accounting reports and long-running feature requests that got shipped over time. If your team has muscle memory on your current tool and it works, that is real value.",
      },
      { type: "h2", text: "Where Viyora is better for a dental clinic" },
      {
        type: "ul",
        items: [
          "Dental-specific: prescription pad with a curated dental drug list, treatment kits, tooth-level charting, warranty templates.",
          "Transparent pricing. You know what you'll pay before you sign anything.",
          "Viyora Copilot included, not a premium add-on.",
          "WhatsApp reminders enabled by default — most Indian patients don't read SMS anymore.",
          "DPDP-first: erasure, portability and data-fiduciary paperwork are baked in.",
        ],
      },
      { type: "h2", text: "What migration actually looks like" },
      {
        type: "ol",
        items: [
          "Export your patient list from your current tool as CSV or Excel (name, phone, DOB, address, medical history).",
          "Start a 30-day Viyora trial and import the file in one click. Our success team helps clean up the columns.",
          "Run one clinic day on Viyora in parallel with your current tool so nothing feels risky.",
          "Once your team is comfortable, cut over. Historical invoices stay in your old tool for your CA; new invoices start numbering from your chosen prefix in Viyora.",
        ],
      },
      { type: "h2", text: "Which should you pick?" },
      {
        type: "p",
        text:
          "If your current software is quietly working and your team has no complaints, there is no urgent reason to switch. But if you find yourself wishing for transparent pricing, WhatsApp-first reminders, dental-specific workflows and an AI assistant that helps you write notes — Viyora is likely the better fit.",
      },
      {
        type: "p",
        text:
          "Either way, take the 30-day trial for a spin. Import your patient list, run one clinic day on Viyora, and decide with your team.",
      },
      { type: "cta" },
    ],
  },

  {
    slug: "reduce-dental-no-shows-whatsapp-reminders-india",
    title: "How to reduce dental no-shows with WhatsApp reminders: an India playbook",
    description:
      "A practical guide to cutting no-shows and last-minute cancellations at Indian dental clinics using WhatsApp reminders, deposit workflows and rebooking automation.",
    date: "2026-01-20",
    author: "Team Viyora",
    readMins: 7,
    category: "Operations",
    cover:
      "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
    coverAlt: "Dentist typing an appointment reminder on a smartphone",
    keywords:
      "reduce dental no shows, whatsapp appointment reminder dentist India, dental clinic no show policy, appointment reminder software India, dental practice management tips",
    sections: [
      {
        type: "p",
        text:
          "No-shows quietly eat a dental practice alive. A missed 45-minute slot is not just lost chair-time — it is lost implant work, a nurse who had to prep, and a patient who could have been in that chair instead. Here is what actually works to bring no-shows down, based on what our clinics do on Viyora and what the published research supports.",
      },
      { type: "h2", text: "Why SMS reminders stopped working" },
      {
        type: "p",
        text:
          "Indian patients do not read SMS. Between spam filters, promotional-message throttling by TRAI, and the fact that most people never open the SMS app, transactional SMS reminders are largely ignored. WhatsApp, on the other hand, is where the conversation already lives — with a reported open rate above 90% for template messages on the Business API.",
      },
      { type: "h2", text: "The three-touch reminder cadence that works" },
      {
        type: "ol",
        items: [
          "24 hours before: a WhatsApp reminder with the dentist's name, the appointment time, the clinic address and a one-tap 'Confirm / Reschedule' button.",
          "2 hours before: a short nudge with a Google Maps link and parking information.",
          "After the appointment: a thank-you message with the prescription PDF, follow-up date and a review request.",
        ],
      },
      {
        type: "p",
        text:
          "Viyora handles all three automatically on the official WhatsApp Business API — no personal number, no risk of ban.",
      },
      { type: "h2", text: "The no-show policy nobody talks about" },
      {
        type: "p",
        text:
          "For procedures over ₹5,000 (root canals, implants, aligners), ask for a 10–20% deposit at the time of booking. Viyora supports Razorpay payment links inside the WhatsApp confirmation flow. Deposit-secured slots see drastically fewer no-shows because the patient has skin in the game.",
      },
      {
        type: "callout",
        title: "A note on tone",
        text:
          "Reminder messages that sound like a bill collector damage the relationship. Keep the language warm, use the patient's first name, and always end with 'reply STOP to opt out' — it is not just polite, it is what the WhatsApp Business Policy requires.",
      },
      { type: "h2", text: "Rebooking the cancellations you cannot save" },
      {
        type: "p",
        text:
          "Some cancellations are unavoidable — the patient's kid gets sick. What you can do is make rebooking effortless. When a patient taps 'Reschedule' in a Viyora reminder, they land on a mini-calendar showing your next three open slots for the same doctor. Ninety seconds later they are re-booked, no phone call needed.",
      },
      { type: "h2", text: "How to measure whether it is actually working" },
      {
        type: "ul",
        items: [
          "Track the no-show rate weekly for the first quarter — Viyora shows this on the dashboard.",
          "Segment by weekday and by doctor. Monday-morning no-shows have very different causes than Saturday-evening ones.",
          "Ask the front desk to log why each no-show happened. Patterns emerge quickly (parking, morning traffic, forgot the appointment).",
        ],
      },
      { type: "h2", text: "Getting started" },
      {
        type: "p",
        text:
          "If you're on Viyora, WhatsApp reminders are already on. If you're not, the 30-day trial includes the WhatsApp automation on the official Business API — enough to see the no-show curve bend in the first month.",
      },
      { type: "cta" },
    ],
  },

  {
    slug: "dpdp-act-2023-dental-clinic-compliance-guide",
    title: "DPDP Act 2023: what every Indian dental clinic must do (2026 checklist)",
    description:
      "A plain-English guide to the Digital Personal Data Protection Act 2023 for dental clinics — consent, erasure, breach notification, and how to become a compliant Data Fiduciary in 2026.",
    date: "2026-01-12",
    author: "Team Viyora",
    readMins: 9,
    category: "Compliance",
    cover:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
    coverAlt: "Legal documents and a stethoscope representing healthcare data compliance",
    keywords:
      "DPDP Act 2023, DPDP compliance for clinics, dental clinic data privacy India, patient data privacy India, DPDP dental clinic checklist",
    sections: [
      {
        type: "p",
        text:
          "The Digital Personal Data Protection Act, 2023 (DPDP) received Presidential assent in August 2023 and is the first cross-sector personal data law India has ever had. Every dental clinic that stores patient names, phone numbers, X-rays or clinical notes electronically is a Data Fiduciary under this Act. This guide translates the parts that apply to you.",
      },
      {
        type: "callout",
        title: "Not legal advice",
        text:
          "We work with a healthcare-privacy counsel and use their guidance for our own product. This post summarises the Act in plain English. For your clinic's specific setup, please consult your lawyer.",
      },
      { type: "h2", text: "The vocabulary you need" },
      {
        type: "ul",
        items: [
          "Data Principal — the patient whose data you hold.",
          "Data Fiduciary — you, the clinic that decides what is done with that data.",
          "Data Processor — any vendor that processes data on your behalf (your practice-management software, your accountant, your cloud storage).",
          "Personal Data — anything that can identify a Data Principal: name, phone, email, Aadhaar, photograph, X-ray, medical history.",
        ],
      },
      { type: "h2", text: "The seven things you must do" },
      {
        type: "ol",
        items: [
          "Give notice: When you collect data, tell the patient exactly what you'll do with it, for how long, and how they can withdraw consent. A patient-registration form is the right place.",
          "Take consent: Consent must be free, specific, informed and unambiguous. A tick-box on the intake form works; a blanket clause buried in fine print does not.",
          "Purpose limitation: Only use the data for the purpose you told the patient. Sharing a mailing list with a marketing agency without fresh consent is not okay.",
          "Enable rights: Patients can ask you to (a) show them their data, (b) correct it, (c) erase it, and (d) nominate someone to receive it after their death. You must respond within a reasonable time.",
          "Report breaches: If patient data is lost or leaked, you must notify the Data Protection Board of India and the affected patients. Speed matters.",
          "Vet your processors: Your software vendor, your accountant and your backup provider are all processors. You are on the hook for their mistakes if you didn't sign an agreement with them.",
          "Delete when done: When the reason you collected the data ends (e.g. the patient asks for erasure, or your retention period ends), you must delete it — including from backups.",
        ],
      },
      { type: "h2", text: "The 'Significant Data Fiduciary' question" },
      {
        type: "p",
        text:
          "The Act allows the Central Government to classify some Data Fiduciaries as 'Significant' based on volume, sensitivity and risk. If you cross the threshold, you'll need to appoint a Data Protection Officer, publish audits, and complete Data Protection Impact Assessments. Most single-location dental clinics will not be classified this way; large multi-city chains may need to be ready.",
      },
      { type: "h2", text: "How Viyora helps you get to compliance" },
      {
        type: "ul",
        items: [
          "Consent capture built into the patient-registration form, with an audit log of when and how consent was given.",
          "One-click data export in JSON so you can honour a portability request in under a minute.",
          "Record-level erasure that also removes data from encrypted backups within the deletion window.",
          "Encrypted-at-rest storage on Indian data centres so nothing leaves the country.",
          "A signed Data Fiduciary–Processor agreement at onboarding, so your paperwork with your software vendor is done.",
        ],
      },
      { type: "h2", text: "Your 2026 checklist" },
      {
        type: "ol",
        items: [
          "Update your intake form with a DPDP-style consent block.",
          "Publish a plain-English privacy notice on your clinic's website.",
          "Sign written processor agreements with every vendor that touches patient data.",
          "Establish an internal process for handling access, correction and erasure requests.",
          "Document a breach-response playbook — who calls whom, in what order, within what hours.",
          "If you cross into 'Significant' territory, appoint a Data Protection Officer.",
        ],
      },
      {
        type: "p",
        text:
          "The DPDP Act is not a threat to Indian dentistry — it is a chance to modernise how patient data is handled. Clinics that lean in early will be trusted more, and audited less.",
      },
      { type: "cta" },
    ],
  },

  {
    slug: "ai-dental-copilot-viyora",
    title: "AI in Indian dentistry: what Viyora's Copilot actually does chairside",
    description:
      "A working dentist's guide to Viyora Copilot — the AI assistant purpose-built for Indian dental practices. What it does, what it doesn't do, and how to use it responsibly on patient data.",
    date: "2026-01-05",
    author: "Team Viyora",
    readMins: 6,
    category: "AI",
    cover:
      "https://images.unsplash.com/photo-1551884170-09fb70a3a2ed?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
    coverAlt: "Dentist looking at an AI-generated treatment summary on a laptop",
    keywords:
      "AI dental software India, AI dental treatment planning, Viyora Copilot, AI in dentistry, dental AI assistant",
    sections: [
      {
        type: "p",
        text:
          "There is a lot of AI theatre in the dental software space right now — vendors slapping ChatGPT on a button and calling it a copilot. Viyora Copilot is genuinely useful because we started from the workflows a dentist runs 40 times a day and worked backwards. Here is what it actually does.",
      },
      { type: "h2", text: "1. Pre-appointment patient briefs" },
      {
        type: "p",
        text:
          "Before every appointment, Copilot generates a two-line summary of the patient: what you did last time, what is due today, what the patient said in the last WhatsApp exchange. You walk into the operatory already knowing where to pick up.",
      },
      { type: "h2", text: "2. Treatment-plan first drafts" },
      {
        type: "p",
        text:
          "Dictate a clinical observation into the notes field — 'lower left first molar deep caries, mesial exposure suspected, sensitivity on cold' — and Copilot suggests a treatment sequence with estimated visits, GST-inclusive fees pulled from your kit prices, and a suggested informed-consent template. You edit, you don't type from scratch.",
      },
      { type: "h2", text: "3. WhatsApp responses when the front-desk is out" },
      {
        type: "p",
        text:
          "When a patient WhatsApps at 9:30 PM asking about their post-op pain, Copilot drafts a reply in the patient's language (English, Hindi, and regional if you enable it), grounded in the clinical notes for that patient. The reply goes to your phone for a one-tap approve.",
      },
      { type: "h2", text: "4. Insurance and follow-up letters" },
      {
        type: "p",
        text:
          "Copilot drafts insurance pre-auth letters, post-op instructions and orthodontic-progress updates, using the specific patient's chart. What used to be a 20-minute Word document at end-of-day is a 30-second review.",
      },
      { type: "h2", text: "What Copilot does not do" },
      {
        type: "ul",
        items: [
          "It does not diagnose. Copilot never produces a diagnosis without a dentist confirming it — this is a clinical safety guardrail we designed on day one.",
          "It does not read X-rays. That is coming as a separate, radiologist-validated module. Copilot will not pretend to interpret an OPG today.",
          "It does not send messages autonomously. Everything Copilot drafts is reviewed by a human before it goes out.",
        ],
      },
      { type: "h2", text: "How your patient's data stays safe" },
      {
        type: "p",
        text:
          "Viyora Copilot is a purpose-built AI assistant for dental workflows. Patient identifiers are stripped in-transit, prompts are logged only against your clinic's audit trail (not against the model), and no data is used for model training. Everything stays inside Viyora's DPDP-compliant infrastructure.",
      },
      {
        type: "callout",
        title: "AI is a copilot, not the pilot",
        text:
          "The dentist decides. Every clinical action produced with Copilot has a human name against it in the audit log — because that is the only way responsible AI in healthcare works.",
      },
      { type: "h2", text: "Try it on your own clinic" },
      {
        type: "p",
        text:
          "The 30-day Viyora trial includes 100 Copilot requests. That is enough to draft treatment plans for a fortnight of patients and decide whether the workflow is real. No credit card, no lock-in.",
      },
      { type: "cta" },
    ],
  },
];

export function getPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug);
}

export function getRelatedPosts(slug, n = 2) {
  return POSTS.filter((p) => p.slug !== slug).slice(0, n);
}

export function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
